import * as React from 'react';
import axios from 'axios';
import {
  Banner,
  DefaultButton,
  DialogActions,
  DialogContent,
  DialogContentText,
  PrimaryButton,
  Typography,
  makeStyles,
} from '@bb-ui/react-library';
import { Dialog } from '@bb-ui/react-library/dist/components/Dialog';
import { DialogProps } from '@bb-ui/react-library/dist/components/Dialog/Dialog.types';
import { DialogTitle } from '@bb-ui/react-library/dist/components/DialogTitle';
import { useAuthContext } from 'contexts/AuthContext';
import { useSnackbar } from 'hooks/useSnackbar';
import { ReleaseStageMember } from 'pages/ReleaseStageMembers/ReleaseStageMembers.types';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from 'utils/apiErrorMessage';
import { apiUrl } from 'utils/apiUrl';

const useStyles = makeStyles((theme) => ({
  siteList: {
    marginBottom: 0,
    maxHeight: 260,
    overflowY: 'auto',
    paddingLeft: theme.spacing(3),
  },
  installationId: {
    color: theme.palette.text.secondary,
  },
  errorBanner: {
    marginTop: theme.spacing(2),
  },
}));

export interface RemoveReleaseStageSitesDialogProps extends DialogProps {
  onClose: () => void;
  /** Called with the tenants that were removed, so the caller can reload its data. */
  onSitesRemoved: (tenantIds: string[]) => void;
  releaseStageId: string;
  releaseStageName: string;
  sites: ReleaseStageMember[];
}

/**
 * Confirms and removes every selected member of a release stage. Each removal is
 * its own request, so a failing site does not stop the rest, and whatever succeeded
 * is reported even when something else failed.
 */
export const RemoveReleaseStageSitesDialog: React.FunctionComponent<
  RemoveReleaseStageSitesDialogProps
> = ({
  id,
  open,
  onClose,
  onSitesRemoved,
  releaseStageId,
  releaseStageName,
  sites,
  ...dialogProps
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { idToken } = useAuthContext();
  const classes = useStyles();
  const [submitting, setSubmitting] = React.useState(false);
  const [errors, setErrors] = React.useState<Array<{ site: ReleaseStageMember; message: string }>>(
    [],
  );
  const authConfig = React.useMemo(
    () => ({ headers: { Authorization: `Bearer ${idToken}` } }),
    [idToken],
  );

  React.useEffect(() => {
    if (!open) {
      setErrors([]);
      setSubmitting(false);
    }
  }, [open]);

  const requestClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  const removeSites = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setErrors([]);

    // Every selected site gets its own attempt, whatever happens to the others.
    const results = await Promise.allSettled(
      sites.map((site) =>
        axios.delete(
          apiUrl(
            'lct',
            `releaseStage/tenants/${encodeURIComponent(site.TenantId)}/stage/${encodeURIComponent(
              releaseStageId,
            )}`,
          ),
          authConfig,
        ),
      ),
    );
    const removedTenantIds: string[] = [];
    const removalErrors: Array<{ site: ReleaseStageMember; message: string }> = [];

    results.forEach((result, index) => {
      const site = sites[index];
      if (result.status === 'fulfilled') {
        removedTenantIds.push(site.TenantId);
        return;
      }

      removalErrors.push({
        site,
        message: getApiErrorMessage(
          result.reason,
          t('releaseStageMembers.removeSitesDialog.removeFailed'),
        ),
      });
    });

    if (removedTenantIds.length > 0) {
      enqueueSnackbar(
        t('releaseStageMembers.removeSitesDialog.removeSucceeded', {
          count: removedTenantIds.length,
        }),
        { variant: 'success' },
      );
      onSitesRemoved(removedTenantIds);
    }

    setErrors(removalErrors);
    setSubmitting(false);

    if (removalErrors.length === 0) {
      onClose();
    }
  };

  return (
    <Dialog
      id={id}
      open={open}
      onClose={requestClose}
      aria-labelledby={`${id}-title`}
      aria-describedby={`${id}-description`}
      isAlert
      {...dialogProps}
    >
      <DialogTitle onClose={requestClose} id={`${id}-title`} closeLabel={t('global.close')}>
        {t('releaseStageMembers.removeSitesDialog.title')}
      </DialogTitle>
      <form onSubmit={removeSites} noValidate data-testid="remove-release-stage-sites-form">
        <DialogContent>
          <DialogContentText id={`${id}-description`}>
            {t('releaseStageMembers.removeSitesDialog.content', {
              count: sites.length,
              releaseStageName,
            })}
          </DialogContentText>
          <ul className={classes.siteList} data-testid="remove-release-stage-sites-list">
            {sites.map((site) => (
              <li key={site.TenantId}>
                {site.Url || site.TenantName}{' '}
                <span className={classes.installationId}>
                  ({site.InstallationId || site.TenantId})
                </span>
              </li>
            ))}
          </ul>
          {errors.length > 0 && (
            <Banner
              severity="error"
              className={classes.errorBanner}
              data-testid="remove-sites-errors"
            >
              <Typography>{t('releaseStageMembers.removeSitesDialog.errorsTitle')}</Typography>
              <ul>
                {errors.map(({ site, message }) => (
                  <li key={site.TenantId}>
                    {site.Url || site.TenantName}: {message}
                  </li>
                ))}
              </ul>
            </Banner>
          )}
        </DialogContent>
        <DialogActions>
          <PrimaryButton type="submit" disabled={submitting || sites.length === 0}>
            {submitting
              ? t('releaseStageMembers.removeSitesDialog.removing')
              : t('releaseStageMembers.removeSitesDialog.confirm')}
          </PrimaryButton>
          <DefaultButton onClick={requestClose} disabled={submitting}>
            {t('global.cancel')}
          </DefaultButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RemoveReleaseStageSitesDialog;
